import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, CircularProgress, IconButton, Tooltip, Typography, Chip } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { palette } from '../../../../../theme/palette';
import { colCenter } from '@ui/style/style';
import { spaceBetween } from './style';
import { ArrowRight, Edit, DocumentText, Archive, ArchiveSlash } from 'iconsax-react';
import { DownloadAction } from '@ui/components/common/actions/DownloadAction';
import { ClientDialog } from './dialog/ClientDialog';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { formatDateMonthDayYear } from '@infrastructure/helpers';
import { downloadCOCDocumentAsync } from '@application/features/freelancer/freelancerThunks';
import { openOrDownloadPdf } from '@application/common/helpers/file-helper';
import { ClientContractStatus, ClientContractStatusMapForAdmin } from '@application/common/enums/client-contract-status';
import DocumentApi from '@infrastructure/document-api';
import AssignmentApi from '@infrastructure/assignment-api';
import { getUserByIdAsync } from '@application/features/adminUsers/adminUserThunk';
const theme = palette('light');
const ScrollAwareTooltip = ({ children, ...props }) => {
    const [open, setOpen] = useState(false);
    useEffect(() => {
        if (!open)
            return;
        const handleScroll = () => setOpen(false);
        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, [open]);
    return (_jsx(Tooltip, { ...props, open: open, onOpen: () => setOpen(true), onClose: () => setOpen(false), children: children }));
};
export const ClientsInformation = ({ showArchived }) => {
    const [isOpen, setisOpen] = React.useState(false);
    const [selectedAssignmentId, setSelectedAssignmentId] = useState(undefined);
    const { user } = useAppSelector((state) => state.user);
    const handleEdit = (assignmentId) => {
        setSelectedAssignmentId(assignmentId);
        setisOpen(true);
    };
    return (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [_jsx(CLientCard, { handleEdit: handleEdit, showArchived: showArchived }), _jsx(ClientDialog, { handleClose: () => setisOpen(false), type: 'edit', open: isOpen, assigmentId: selectedAssignmentId, contractType: user?.contractType })] }));
};
export const CLientCard = ({ handleEdit, showArchived }) => {
    const { user } = useAppSelector((state) => state.user);
    const dispatch = useAppDispatch();
    const [loadingStates, setLoadingStates] = useState({});
    const [archiveLoadingStates, setArchiveLoadingStates] = useState({});
    const handleArchiveToggle = async (assignmentId, isArchived) => {
        setArchiveLoadingStates((prev) => ({ ...prev, [assignmentId]: true }));
        try {
            await AssignmentApi.updateAssignmentArchiveStatus(assignmentId, isArchived);
            if (user?.id) {
                dispatch(getUserByIdAsync(user.id));
            }
        }
        finally {
            setArchiveLoadingStates((prev) => ({ ...prev, [assignmentId]: false }));
        }
    };
    const sortedAssignments = useMemo(() => {
        const statusOrder = {
            [ClientContractStatus.Signed]: 0,
            [ClientContractStatus.Approved]: 1,
            [ClientContractStatus.PendingSignature]: 2,
            [ClientContractStatus.Reason]: 3,
            [ClientContractStatus.Rejected]: 4,
            [ClientContractStatus.Expired]: 5
        };
        return [...(user?.assignments ?? [])].sort((a, b) => {
            const aStatusRank = statusOrder[a.status] ?? Number.MAX_SAFE_INTEGER;
            const bStatusRank = statusOrder[b.status] ?? Number.MAX_SAFE_INTEGER;
            if (aStatusRank !== bStatusRank) {
                return aStatusRank - bStatusRank;
            }
            const aTime = new Date(a.contactStartDate ?? a.contactEndDate ?? 0).getTime();
            const bTime = new Date(b.contactStartDate ?? b.contactEndDate ?? 0).getTime();
            return bTime - aTime;
        });
    }, [user?.assignments]);
    const filteredAssignments = useMemo(() => {
        return sortedAssignments.filter((a) => (showArchived ? a.isArchived === true : a.isArchived !== true));
    }, [sortedAssignments, showArchived]);
    const downloadContract = (contract) => {
        const fileDto = { folderName: contract?.folderName, fileName: contract?.name };
        dispatch(downloadCOCDocumentAsync(fileDto))
            .unwrap()
            .then((res) => {
            openOrDownloadPdf(res.file, contract?.name ?? 'document', 'Client Contract');
        });
    };
    const setIsLoading = (assignmentId, isLoading) => {
        setLoadingStates((prev) => ({ ...prev, [assignmentId]: isLoading }));
    };
    const getIsLoading = (assignmentId) => {
        return loadingStates[assignmentId] || false;
    };
    return (_jsx(_Fragment, { children: filteredAssignments.length ? (filteredAssignments.map((assignment, index) => (_jsxs(Box, { mt: 2, sx: {
                background: 'white',
                borderRadius: '16px',
                padding: '20px'
            }, children: [_jsxs(Box, { sx: { mb: 2 }, children: [_jsxs(Box, { sx: { ...spaceBetween }, children: [_jsx(Typography, { variant: 'h6', color: 'initial', children: assignment.clientName }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center' }, children: [assignment.status !== ClientContractStatus.Expired && (_jsx(ScrollAwareTooltip, { title: 'Edit Assignment', children: _jsx(IconButton, { onClick: () => {
                                                    handleEdit(assignment.id);
                                                }, children: _jsx(Edit, {}) }) })), !assignment.isArchived ? (_jsx(ScrollAwareTooltip, { title: 'Archive Assignment', children: _jsx(IconButton, { disabled: !!archiveLoadingStates[assignment.id], onClick: () => handleArchiveToggle(assignment.id, true), children: _jsx(Archive, { size: 20 }) }) })) : (_jsx(ScrollAwareTooltip, { title: 'Unarchive Assignment', children: _jsx(IconButton, { disabled: !!archiveLoadingStates[assignment.id], onClick: () => handleArchiveToggle(assignment.id, false), children: _jsx(ArchiveSlash, { size: 20 }) }) }))] })] }), _jsxs(Box, { sx: { ...colCenter, gap: 0.5 }, children: [_jsx(Typography, { variant: 'subtitle2', color: 'initial', children: formatDateMonthDayYear(assignment.contactStartDate) }), _jsx(ArrowRight, { size: '14px' }), _jsx(Typography, { variant: 'subtitle2', color: 'initial', children: formatDateMonthDayYear(assignment.contactEndDate) })] }), _jsx(Typography, { variant: 'subtitle2', color: 'initial', children: user?.position }), _jsx(Typography, { variant: 'subtitle2', color: 'initial', children: assignment.contactPersonFullName }), (() => {
                            // Check if there's a DocuSign signed document (contract with autoGeneratedContractLink)
                            const docuSignContract = assignment.clientContracts?.find((contract) => contract.autoGeneratedContractLink);
                            // Show status button for Approved, Signed, Rejected, and PendingSignature
                            const shouldShowStatusButton = assignment.status === ClientContractStatus.Signed ||
                                assignment.status === ClientContractStatus.Approved ||
                                assignment.status === ClientContractStatus.Expired ||
                                assignment.status === ClientContractStatus.Rejected ||
                                assignment.status === ClientContractStatus.PendingSignature;
                            if (shouldShowStatusButton) {
                                // Only show link for Signed and Approved statuses with DocuSign contract
                                const hasLink = docuSignContract?.autoGeneratedContractLink &&
                                    (assignment.status === ClientContractStatus.Signed ||
                                        assignment.status === ClientContractStatus.Approved);
                                const statusTag = ClientContractStatusMapForAdmin.get(assignment.status);
                                // Show DownloadAction for Approved/Signed with link
                                if (hasLink) {
                                    const isLoading = getIsLoading(assignment.id);
                                    return (_jsxs(Box, { mt: 1, position: 'relative', children: [_jsx(DownloadAction, { width: '100%', fileName: '', type: 'download', tag: statusTag, disabled: isLoading, onClick: async () => {
                                                    setIsLoading(assignment.id, true);
                                                    try {
                                                        // Get a fresh signing URL on-demand (works even if stored URL expired)
                                                        const response = await DocumentApi.getDocuSignSigningUrl(assignment.id);
                                                        if (response.data?.signingUrl) {
                                                            window.open(response.data.signingUrl, '_blank', 'noopener,noreferrer');
                                                        }
                                                        else {
                                                            // Fallback to stored URL if API fails
                                                            window.open(docuSignContract.autoGeneratedContractLink, '_blank', 'noopener,noreferrer');
                                                        }
                                                    }
                                                    catch (error) {
                                                        // Fallback to stored URL if API fails
                                                        window.open(docuSignContract.autoGeneratedContractLink, '_blank', 'noopener,noreferrer');
                                                    }
                                                    finally {
                                                        setIsLoading(assignment.id, false);
                                                    }
                                                } }), isLoading && (_jsx(Box, { sx: {
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    zIndex: 1
                                                }, children: _jsx(CircularProgress, { size: 20 }) }))] }));
                                }
                                // Show simple div for Rejected, Expired and PendingSignature (no link)
                                return (_jsx(Box, { mt: 1, sx: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: 'black',
                                        background: theme.primary.light,
                                        borderRadius: '13px',
                                        width: '100%',
                                        position: 'relative',
                                        padding: '10px 15px',
                                        cursor: 'default'
                                    }, children: _jsxs(Box, { display: 'flex', gap: 1, alignItems: 'center', children: [_jsx(Box, { sx: { ...colCenter }, children: _jsx(DocumentText, {}) }), _jsxs(Box, { sx: {
                                                    textAlign: 'left',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    flexGrow: 1
                                                }, children: [_jsx(Typography, { variant: 'caption', sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, color: 'inherit' }), _jsx(Box, { sx: { ...colCenter, gap: 1 }, children: statusTag && (_jsx(Chip, { label: statusTag, variant: 'soft', sx: { borderRadius: '20px' }, size: 'small' })) })] })] }) }));
                            }
                            return null;
                        })()] }, index), ' '] })))) : (_jsx(Typography, { variant: 'body1', color: 'initial', children: "No assignments available." })) }));
};
